import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { Box } from '@bridebook/ui';
import FullpageLoader from '@bridebook/web/app-shared/components/fullpage-loader/fullpage-loader';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import ConnectionStatus from '@bridebook/web/components/app/connection-status';
import Head from '@bridebook/web/components/app/head';
import Snackbar from '@bridebook/web/components/app/snackbar-wrapper';
import { AIPlannerButton } from '@bridebook/web/components/ai-planner/ai-planner-button';
import AIPlannerPanelLazy from '@bridebook/web/components/ai-planner/ai-planner-panel-lazy';
import { AIPlannerProvider } from '@bridebook/web/components/ai-planner/ai-planner-provider';
import { useAuthContext } from '@bridebook/web/components/auth/auth-context';
import WarningModal from '@bridebook/web/components/bbcommon/warning-modal/warning-modal-lazy';
import BookPhotographerModalLazy from '@bridebook/web/components/book-photographer/book-photographer-modal-lazy';
import EnquiriesUserLimitModal from '@bridebook/web/components/enquiries/enquiries-user-limit/enquiries-user-limit-modal/enquiries-user-limit-modal-lazy';
import EnquiryModalCollection from '@bridebook/web/components/enquiries/enquiry-modal-collection';
import LoadingBar from '@bridebook/web/components/loading-bar';
import { PageContent } from '@bridebook/web/components/page-content';
import AtYourVenueQuiz from '@bridebook/web/components/quiz/at-your-venue-quiz/at-your-venue-quiz-lazy';
import CakesQuiz from '@bridebook/web/components/quiz/cakes-quiz/cakes-quiz-lazy';
import PhotoQuiz from '@bridebook/web/components/quiz/photo-quiz/photo-quiz-lazy';
import FeedbackPopup from '@bridebook/web/components/settings/feedback-popup/feedback-popup-lazy';
import VenueConfirmationModal from '@bridebook/web/components/venue-confirmation/venue-confirmation-modal';
import ViewVenuerexPopup from '@bridebook/web/components/venuerex/view-venuerex-popup/view-venuerex-popup-lazy';
import FetchNewestDraftModal from '@bridebook/web/components/wedding-website/shared/modals/fetch-newest-draft-modal-lazy';
import {
  getIsAIPlannerDocked,
  getIsAIPlannerExpanded,
  getIsAIPlannerPanelRestored,
} from '@bridebook/web/lib/ai-planner/selectors';
import { setPathname } from '@bridebook/web/lib/app/actions';
import ComponentLevelErrorBoundary from '@bridebook/web/lib/app/component-level-error-boundary';
import { getLastPath } from '@bridebook/web/lib/app/selectors';
import { hideSplashScreen } from '@bridebook/web/lib/mobile-app/actions';
import {
  defaultPageSettings,
  IPageSettings,
  PageSettings,
} from '@bridebook/web/lib/page-settings-context';
import { mapUrlSlugToSupplierType } from '@bridebook/toolbox/src/mapSlugToFriendlyUrlSlug';
import type { UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { updateSearchCategory } from '@bridebook/web/lib/search/actions';
import {
  checkIsSearchLocationInitialized,
  getSearchRequestSlug,
} from '@bridebook/web/lib/search/selectors';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector, useStore } from '@bridebook/web/lib/utils';
import useOfflineDetection from '@bridebook/web/lib/utils/hooks/use-offline-detection';
import { usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import { getIsProfileLoading, getProfileLoaded } from '@bridebook/web/lib/weddings/selectors';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import Router, { useRouter } from 'next/router';
import { ComponentType, Fragment, FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { StickyContainer } from 'react-sticky';
import ShowroundModal from '../components/showround/containers/showround-modal-lazy';
import MultiShowroundModal from '../components/showround/multi-showround/multi-showround-modal-lazy';
import { ShowroundPromoModalLazy } from '@bridebook/web/components/showround/containers/showround-promo-modal/showround-promo-modal-lazy';
import { DownloadAppBanner } from './download-app-banner/download-app-banner';
import { LostLeadModal } from './lost-lead-modal/lost-lead-modal-lazy';
import componentStyles from './page.style';
import Providers from './providers';
import BeautyQuiz from './quiz/beauty-quiz/beauty-quiz-lazy';
import FloristsQuiz from './quiz/florists-quiz/florists-quiz-lazy';
import VideoQuiz from './quiz/video-quiz/video-quiz-lazy';
import SearchModalLazy from './search/search-modal-lazy';
import ShortlistModalCollectionLazy from './shortlist/shortlist-modal-lazy-collection';
import CsatFeedbackDrawerLazy from './csat-feedback/csat-feedback-lazy';
import { isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import { searchResultsPagePrefix } from '@bridebook/web/lib/search/constants';

if (process.browser) {
  require('smoothscroll-polyfill').polyfill(); // eslint-disable-line
}

export interface IPageProps {
  reservedStickySpace?: boolean;
  isSticky?: boolean;
  hideFooter?: boolean;
  hideFooterTop?: boolean;
  isSettingsCordova?: boolean;
  isSignupPage?: boolean;
  stickyHeader?: boolean;
  children?: React.ReactNode;
  childrenContentWrap?: React.ReactNode;
  pageSettings?: IPageSettings;
  subHeaderContent?: ReactNode;
  isOnboardingMobile?: boolean;
  hideHeader?: boolean;
  customHeader?: ReactNode;
  disableSafeAreaInsetTop?: boolean;
  /**
   * A provider for the page content and its custom header. It goes here rather than around
   * the layout: a different root element per route would remount the whole page shell and
   * everything it holds, such as the AI planner conversation.
   */
  contentWrapper?: ComponentType<{ children: ReactNode }>;
  contentOffset?: {
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
}

interface IProps extends IPageProps {}

const Page: FunctionComponent<IProps> = ({
  children,
  childrenContentWrap,
  reservedStickySpace,
  isSticky,
  hideFooter,
  hideFooterTop,
  isSettingsCordova,
  isSignupPage,
  stickyHeader,
  pageSettings = defaultPageSettings,
  subHeaderContent,
  isOnboardingMobile,
  hideHeader,
  customHeader,
  contentOffset = {},
  disableSafeAreaInsetTop = false,
  contentWrapper: ContentWrapper = Fragment,
}: IProps) => {
  const dispatch = useDispatch();
  const pathname = usePathWithoutMarket();
  const store = useStore();
  const isProfileLoaded = useSelector(getProfileLoaded);
  const isSearchLocationInitialized = useSelector(checkIsSearchLocationInitialized);
  const market = useMarket();
  const { isInitialized, isAuthenticated, user, wedding } = useAuthContext();
  const router = useRouter();
  const { isFallback, isReady: isRouterReady } = router;
  const isProfileLoading = useSelector(getIsProfileLoading);
  const lastPath = useSelector(getLastPath);

  /*
    This effect adds a listener to popstate event, which is triggered when
    the user navigates back or forward in the history. It handles:
    1. Redirecting away from onboarding if user has already passed it
    2. Correcting search category slug when it differs from Redux state
       (e.g. after booking a venue, back button should lead to photographers search)
    3. Fixing market prefix mismatches (e.g. after changing wedding country in settings)
  */
  useEffect(() => {
    Router.beforePopState(({ as }) => {
      // If the last path (the one we're about to navigate to) is onboarding and the current one isn't, navigate to the home page
      if (!router.pathname.includes('onboarding') && as.includes('/onboarding')) {
        router.push(UrlHelper.home);
        return false;
      }

      // Sync Redux search category with the URL's category on back/forward navigation
      if (isSearchResultsPage(as)) {
        const urlSlug = as.split(searchResultsPagePrefix)[1]?.split(/[/?#]/)[0];
        if (urlSlug) {
          const supplierType = mapUrlSlugToSupplierType(urlSlug as UrlFriendlySlug);
          if (supplierType && supplierType !== getSearchRequestSlug(store.getState())) {
            store.dispatch(updateSearchCategory(supplierType, false));
          }
        }
      }

      if (as.startsWith(`/${market.prefix}`)) return true;

      const asSplit = as.split('/');

      // only cont. processing if this is actually a market prefix
      if (asSplit?.[1]?.match(/^[a-z]{2}(?:-[a-z]{2}){0,2}$/)) {
        asSplit[1] = market.prefix;
        location.href = asSplit.join('/');
        return false;
      }

      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [router, market, lastPath, store]);

  /*
   Set pathname on asPath change
   TODO: this should be handled in an epic
  */
  useEffect(() => {
    dispatch(setPathname(pathname));
  }, [pathname, dispatch]);

  const queryClient = useQueryClient();

  /*
   Clear React Query cache on logout (when profile is not loaded)
  */
  useEffect(() => {
    if (!isProfileLoaded) {
      queryClient.clear();
    }
  }, [queryClient, isProfileLoaded]);

  const isOffline = useOfflineDetection();
  const isPlannerDocked = useSelector(getIsAIPlannerDocked);
  const isPlannerExpanded = useSelector(getIsAIPlannerExpanded);
  const isPlannerRestored = useSelector(getIsAIPlannerPanelRestored);

  const styles = componentStyles({
    isPlannerDocked,
    isPlannerExpanded,
    isPlannerAnimated: !isPlannerRestored,
  });

  const showHeader = !isOnboardingMobile && !hideHeader;

  const content = (
    <ContentWrapper>
      <PageContent
        {...{
          childrenContentWrap,
          contentOffset,
          customHeader,
          disableSafeAreaInsetTop,
          hideFooter,
          hideFooterTop,
          isSettingsCordova,
          pageSettings,
          reservedStickySpace,
          showHeader,
          stickyHeader,
          subHeaderContent,
        }}>
        {children}
      </PageContent>
    </ContentWrapper>
  );

  /**
   * Save initial loader value to local state. This is needed in order to hide it after 25s
   * if it's still shown. This is a workaround for a bug where the loader is not hidden after
   * certain amount of time. The source of the bug remain unknown for now.
   */
  const showLoaderInitial =
    isFallback ||
    !isProfileLoaded ||
    !isSearchLocationInitialized ||
    !isRouterReady ||
    !isInitialized;

  const [showLoader, setShowLoader] = useState(showLoaderInitial);

  useEffect(() => {
    if (!showLoaderInitial) {
      setShowLoader(false);
    }
  }, [showLoaderInitial]);

  useEffect(() => {
    if (showLoader && !isSignupPage) {
      const timeout = setTimeout(() => {
        try {
          const state = store.getState();
          const isProfileLoaded = getProfileLoaded(state);
          const isSearchLocationInitialized = checkIsSearchLocationInitialized(state);

          SentryMinimal().captureMessage('[Page.tsx] App failed to hide loader after 25 seconds', {
            extra: {
              state: {
                isRouterReady: Router.isReady,
                isRouterFallback: Router.isFallback,
                isProfileLoaded,
                appState: state.app,
                isSearchLocationInitialized,
                isNativePlatform: Capacitor.isNativePlatform(),
                isInitialized,
                isAuthenticated,
                isProfileLoading,
                userId: user?.id,
                weddingId: wedding?.id,
              },
            },
            tags: {
              source: 'AppFailedToLoad',
            },
          });
        } finally {
          setShowLoader(false);
          dispatch(hideSplashScreen());
        }
      }, 30 * 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [
    dispatch,
    isInitialized,
    isSignupPage,
    showLoader,
    store,
    user?.id,
    wedding?.id,
    isAuthenticated,
    isProfileLoading,
  ]);

  return (
    <PageSettings.Provider value={pageSettings}>
      <Providers>
        <>
          <Snackbar />
          {isOffline && <ConnectionStatus />}
          <LoadingBar />
          <ComponentLevelErrorBoundary component="Page">
            {isSignupPage && <>{content}</>}
            {!isSignupPage && showLoader && (
              <>
                <Head />
                <FullpageLoader />
              </>
            )}
            {!isSignupPage && !showLoader && (
              <AIPlannerProvider>
                <DownloadAppBanner />
                <Box
                  w="100%"
                  minH="100dvh"
                  style={styles.containerContentWrap}
                  id="page-container"
                  // The page's own width for `@screen-md/main:` and the like: a docked AI
                  // planner narrows it while `md:` still sees the whole window.
                  className="@container/main">
                  {isSticky ? <StickyContainer>{content}</StickyContainer> : content}
                </Box>

                <EnquiryModalCollection />
                <WarningModal />
                <ViewVenuerexPopup />
                <>
                  <BeautyQuiz />
                  <FloristsQuiz />
                  <PhotoQuiz />
                  <CakesQuiz />
                  <AtYourVenueQuiz />
                  <VideoQuiz />
                  <BookPhotographerModalLazy />
                  <EnquiriesUserLimitModal />
                  <FeedbackPopup />
                  <VenueConfirmationModal />
                  <LostLeadModal />
                  <ShortlistModalCollectionLazy />
                  <SearchModalLazy />
                  <ShowroundModal />
                  <MultiShowroundModal />
                  <ShowroundPromoModalLazy />
                  <FetchNewestDraftModal />
                  <CsatFeedbackDrawerLazy />
                  <AIPlannerButton />
                  <AIPlannerPanelLazy />
                </>
              </AIPlannerProvider>
            )}
          </ComponentLevelErrorBoundary>
        </>
      </Providers>
    </PageSettings.Provider>
  );
};

export default Page;
